import type { GetListParams, Identifier } from "ra-core";

/** Meetings whose text matches a search, most recent first: one RPC's answer. */
export type IdPage = {
  ids: Identifier[];
  /** True when more meetings matched than were returned. */
  capped: boolean;
};

/**
 * Meetings whose transcript or summary text matches a search. `ids` is the
 * union the list filters on; the two sets say where each one matched, which
 * decides the excerpt it gets. LCATOMICZO-76 phases 4 and 5.
 */
export type MeetingTextIds = IdPage & {
  transcriptIds: Identifier[];
  summaryIds: Identifier[];
};

export type SummaryExcerpt = {
  meeting_id: Identifier;
  /** The summaryEntries() key the excerpt came from, so a link can open it. */
  entry_key: string;
  /** "Recap", "By topic: <heading>", "Next steps" or a saved summary's label. */
  entry_label: string;
  /** Marked the same way as TranscriptExcerpt. */
  excerpt: string;
};

/**
 * Joins the transcript and summary id searches. One side failing is logged and
 * the other still answers; only both failing throws, which the list turns into
 * a plain field search.
 */
export const combineMeetingTextIds = (
  transcript: PromiseSettledResult<IdPage>,
  summary: PromiseSettledResult<IdPage>,
): MeetingTextIds => {
  if (transcript.status === "rejected" && summary.status === "rejected") {
    throw transcript.reason;
  }
  if (transcript.status === "rejected") {
    console.error("Transcript search failed", transcript.reason);
  }
  if (summary.status === "rejected") {
    console.error("Summary search failed", summary.reason);
  }
  const t = transcript.status === "fulfilled" ? transcript.value : undefined;
  const s = summary.status === "fulfilled" ? summary.value : undefined;
  const transcriptIds = t?.ids ?? [];
  const summaryIds = s?.ids ?? [];
  const seen = new Set<string>();
  const ids: Identifier[] = [];
  for (const id of [...transcriptIds, ...summaryIds]) {
    if (seen.has(String(id))) continue;
    seen.add(String(id));
    ids.push(id);
  }
  return {
    ids,
    transcriptIds,
    summaryIds,
    capped: !!t?.capped || !!s?.capped,
  };
};

export type TranscriptExcerpt = {
  meeting_id: Identifier;
  /** Seconds into the transcript where the best-matching cue starts. */
  cue_start: number;
  /** The words around it, matched words wrapped in  and . */
  excerpt: string;
};

export const TRANSCRIPT_ID_LIMIT = 200;

/** Searches shorter than this skip the transcript and summary calls. */
export const MIN_TRANSCRIPT_QUERY = 3;

export const transcriptQuery = (q: unknown): string | null => {
  if (typeof q !== "string") return null;
  const trimmed = q.trim();
  return trimmed.length >= MIN_TRANSCRIPT_QUERY ? trimmed : null;
};

/**
 * Adds transcript matches to the list's text search. applyFullTextSearch has
 * already turned `q` into an `@or` of `column@ilike` entries; the ids join that
 * same `@or`, so a meeting is listed when either its fields or its transcript
 * match, and every other filter still applies to both.
 */
export const withTranscriptIds = (
  params: GetListParams,
  ids: Identifier[],
): GetListParams => {
  const or = params.filter?.["@or"];
  if (!ids.length || !or || typeof or !== "object") return params;
  return {
    ...params,
    filter: {
      ...params.filter,
      "@or": { ...or, "id@in": `(${ids.join(",")})` },
    },
  };
};

/**
 * Single-flights the id search between the list's data provider call and the
 * excerpt hook, which fire for the same settled query at the same moment. It
 * caches the in-flight promise, not the result, so the second caller joins the
 * first request instead of starting its own. A failed request is dropped at
 * once so the next search retries it.
 */
export const createTranscriptIdsMemo = (
  fetch: (query: string) => Promise<MeetingTextIds>,
  ttlMs = 30_000,
  now: () => number = Date.now,
) => {
  const entries = new Map<
    string,
    { at: number; promise: Promise<MeetingTextIds> }
  >();
  return (query: string): Promise<MeetingTextIds> => {
    const hit = entries.get(query);
    if (hit && now() - hit.at < ttlMs) return hit.promise;
    const promise = fetch(query).catch((error) => {
      if (entries.get(query)?.promise === promise) entries.delete(query);
      throw error;
    });
    entries.set(query, { at: now(), promise });
    return promise;
  };
};
